import { Exception, ToasterService } from '@anddone/ui-utilities';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { TableDetails } from '../../shared/tablefilters/table-details.model';
import { Column } from '../../shared/tablefilters/column.model';
import { Router } from '@angular/router';
import { DeveloperService } from '../../../../../services/developer.service';
import { TableFilterService } from '../../shared/tablefilters/table-filter.service';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { MessageConstants } from '../../../../../constants/message.constant';
import { TableComponent } from '../../shared/tablefilters/table/table.component';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { ValidationConstant } from '../../../../../constants/validation.constant';

@Component({
  selector: 'app-find-webhook-event-logs',
  imports: [TranslateModule, TableComponent],
  templateUrl: './find-webhook-event-logs.html',
  styleUrl: './find-webhook-event-logs.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
})
export class FindWebhookEventLogs implements OnInit, OnDestroy {
  selectedWebhook: any;

  /*** Local variables needed for table filter system */
  searchParamsData: any = {};
  pager: any = {};
  tableActionSub!: Subscription;
  searchInputSub!: Subscription;
  private destroy$ = new Subject<void>();

  /**** Defined table details & columns ****/
  tableDetails: TableDetails = {
    tableId: 'webhook-event-log-table',
    searchPlaceholder: 'Log ID, URL',
    allowRowClick: true,
    rowClickURI: 'ally/webhook-details',
    tableTitle: 'Webhook Events',
    defaultItemPerPage: ValidationConstant.defaultPageSize,
    sendDefault90DayFilter: true, // If no other filters are selected, send a filter limiting to 90 days
    default90DayFilterFromRequestKey: 'StartDate',
    default90DayFilterToRequestKey: 'EndDate',
  };

  columns: Column[] = [
    {
      name: 'Log Id',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      responseKey: 'id',
      requestKey: 'LogId',
      filter: {
        type: 'string',
      },
    },
    {
      name: 'URL',
      sortState: 'desc',
      isSortable: true,
      display: 'normal',
      responseKey: 'webhookUrl',
      requestKey: 'WebhookUrl',
      filter: {
        type: 'string',
      },
    },
    {
      name: 'Module',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      responseKey: 'moduleName',
      requestKey: 'ModuleName',
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'Report', key: 'Report', checked: false },
          { label: 'Transaction', key: 'Transaction', checked: false },
          { label: 'Payment Link', key: 'PaymentLink', checked: false },
          { label: 'Account Token', key: 'AccountToken', checked: false },
          { label: 'Token Link', key: 'TokenLink', checked: false },
          { label: 'CBNacha', key: 'CBNacha', checked: false },
          { label: 'Premium Finance', key: 'PremiumFinance', checked: false },
          { label: 'Refund', key: 'TransactionRefund', checked: false },
          { label: 'Chargeback', key: 'TransactionChargeback', checked: false },
          { label: 'Void', key: 'TransactionVoid', checked: false },
          { label: 'Merchant Management', key: 'MerchantManagement', checked: false },
          { label: 'Account Payout', key: 'AccountPayout', checked: false },
        ],
      },
    },
    {
      name: 'Event',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'eventName',
      requestKey: 'EventName',
      filter: undefined,
    },
    {
      name: 'Transaction ID',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      responseKey: 'transactionId',
      requestKey: 'transactionId',
      filter: {
        type: 'string',
      },
    },
    {
      name: 'Date Time',
      sortState: 'none',
      isSortable: true,
      display: 'date',
      responseKey: 'createdOn',
      requestKey: null, // request keys set in filter for dates.
      filter: {
        type: 'date',
        dateFromRequestKey: 'StartDate',
        dateToRequestKey: 'EndDate',
      },
    },
    {
      name: 'Status',
      sortState: 'none',
      isSortable: false,
      display: 'status',
      responseKey: 'status',
      requestKey: 'Status',
      statusColors: {
        Sent: 'green',
        Suspended: 'purple',
        Failed: 'red',
        Pending: 'yellow',
        'In Progress': 'blue',
      },
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'Sent', key: 'Sent', checked: false },
          { label: 'Failed', key: 'Failed', checked: false },
          { label: 'Suspended', key: 'Suspended', checked: false },
          { label: 'Pending', key: 'Pending', checked: false },
          { label: 'In Progress', key: 'InProgress', checked: false },
        ],
      },
    },
    {
      name: 'Actions',
      sortState: 'none',
      isSortable: false,
      display: 'actions',
      responseKey: 'operations',
      requestKey: null,
      filter: undefined,
    },
  ];
  /*** End of table filter definitions ***/

  private router = inject(Router);
  private allyCommonService = inject(AllyCommonService);
  private developerService = inject(DeveloperService);
  private toasterService = inject(ToasterService);
  private tableFilterService = inject(TableFilterService);

  ngOnInit(): void {
    this.selectedWebhook = this.developerService.getSelectedWebhook();

    this.developerService.webhookDetailUpdate.subscribe((status) => {
      if (status === true) {
        this.findLogs(1);
      }
    });

    /*** Table Filter subscriptions ***/
    this.tableFilterService.init(this.columns, this.tableDetails);

    this.tableActionSub = this.tableFilterService.tableActionEvent.subscribe((operationObject) => {
      this.actionTriggered(operationObject.operation.value, operationObject.row);
    });

    this.searchInputSub = this.tableFilterService.searchInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchInput) => {
        this.searchParamsData = searchInput.searchParams;
        this.pager = searchInput.pager;

        if (this.selectedWebhook.source._value) {
          // if page is redirected from find webhooks
          this.searchParamsData.ModuleName = this.selectedWebhook.source._value.moduleName;
        }

        /*** Execute the method that manages your API call here. ***/
        this.findLogs(this.pager.currentPage);
      });

    this.tableFilterService.registerSubscriptions(this.searchInputSub, this.tableActionSub);
    /*** End of Table filter subscription handling ***/
  }

  findLogs(pageNumber: number) {
    if (pageNumber <= 0) {
      return;
    }

    /*** prepared the searchParamsData object to be sent to the API call */
    this.searchParamsData = this.tableFilterService.prepareSearchParamsForAPICall(pageNumber);
    this.allyCommonService.setIsLoader(true);

    this.developerService
      .getAllWebhookEventLog(this.searchParamsData)
      ?.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.data.length == 0) {
            this.pager = this.tableFilterService.setPager(res, pageNumber, this.pager);
            this.allyCommonService.setIsLoader(false);

            /*** Update the tableData toshow no results  ***/
            this.tableDetails.tableData = [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          } else {
            this.pager = this.tableFilterService.setPager(res, pageNumber, this.pager);
            res.data.forEach((ele: any) => {
              ele.operations = [];
              /*** Configure operations for table actions row ***/
              ele.operations.push({ key: 'retrigger', value: 'Retrigger' });
              ele.operations.push({ key: 'view', value: 'View' });
              ele.eventName = ele.eventName?.split('.')[1];
              if (ele.module == 'Transaction') {
                ele.transactionId = ele.entityId;
              } else {
                ele.transactionId = '--';
              }
            });

            /*** Set the table details to include data to be displayed and pagination data and send to tablefilter service */
            this.tableDetails.tableData = res.data || [];

            /*** Set the table details to include data to be displayed and pagination data and send to tablefilter service */
            this.tableFilterService.setTableDetails(this.tableDetails);

            this.allyCommonService.setIsLoader(false);
          }
        },

        error: (e) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  ngOnDestroy(): void {
    this.developerService.setSelectedWebhook(null);
    this.destroy$.next(); // Triggers takeUntil
    this.destroy$.complete(); // Final cleanup
  }

  // retrigger actions on webhook event log
  actionTriggered(selectedActionType: string, data?: any) {
    this.allyCommonService.setIsLoader(true);
    if (selectedActionType === 'Retrigger') {
      const retriggerObj = {
        entityId: data?.entityId,
        eventId: data?.eventId,
        subscriptionId: data?.subscriptionId,
      };

      this.developerService.retriggerWebhookEventLogAction(retriggerObj)?.subscribe({
        next: (_res: any) => {
          this.findLogs(this.pager.currentPage);
          this.toasterService.showSuccess(MessageConstants.developer.RetriggerSuccess, 'full');
          this.allyCommonService.setIsLoader(false);
        },
        error: (e) => {
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
          this.allyCommonService.setIsLoader(false);
        },
      });
    } else if (selectedActionType === 'View') {
      this.router.navigate(['/ally/webhook-details/', data.id]);
    }
  }
}
